// 의류(CLOTHES) 상품명 작명 규칙 가이드 — 2026.07 확정 (전수 스캔 2,808건 기반), 2026.9.11 치마→스커트 표준 전환·점프수트 추가
// 규칙 원문: 워크스페이스 루트 PRODUCT_NAMING_GUIDE.md
// ⚠ malladmin·manageradmin에도 동일 사본이 있음 — 규칙 변경 시 함께 수정할 것
// 형식: 브랜드명(한글) [모델번호] [성별] 특징 수식어 아이템 종류 (시즌 표기는 제외 — 2026.7.21 결정)

export const CLOTHES_NAME_FORMAT =
  "브랜드명 → 모델번호 → (성별) → 특징 → 아이템 종류";
export const CLOTHES_NAME_EXAMPLE = "버버리 8071071 트러스필드 바람막이 자켓";

// 아이템 종류 표준 목록 (가나다순) — 구성 입력 드롭다운의 단일 소스
// 값 추가/변경 시 PRODUCT_NAMING_GUIDE.md의 표준어 표와 함께 수정할 것
export const CLOTHES_ITEM_LIST = [
  "가디건",
  "니트",
  "롱패딩",
  "맨투맨",
  "바지",
  "반바지",
  "블라우스",
  "셔츠",
  "스웨터",
  "스커트",
  "슬랙스",
  "야상",
  "원피스",
  "자켓",
  "점퍼",
  "점프수트",
  "조끼",
  "집업",
  "청바지",
  "코트",
  "트렌치코트",
  "티셔츠",
  "파카",
  "패딩",
  "후드티",
];

// 동의어 병기(키워드 스터핑) 감지 쌍 — 인접해서 나란히 쓰면 경고
const SYNONYM_PAIRS = [
  ["스커트", "치마"],
  ["바지", "팬츠"],
  ["원피스", "드레스"],
  ["조끼", "베스트"],
  ["셔츠", "블라우스"],
  ["니트", "스웨터"],
  ["모자", "캡"],
  ["자켓", "재킷"],
  ["가디건", "카디건"],
  ["바람막이", "윈드브레이커"],
  ["점프수트", "점프슈트"],
];

// 비표준 표기 → 표준 표기 (어느 위치든 경고)
const NONSTANDARD_WORDS = {
  재킷: "자켓",
  카디건: "가디건",
};

// 마지막 아이템 종류 통일 (끝 단어일 때만 경고)
const NONSTANDARD_LAST = {
  치마: "스커트",
  팬츠: "바지",
  점프슈트: "점프수트",
};

const COLOR_WORDS =
  /(블랙|화이트|네이비|베이지|그레이|브라운|카키|레드|블루|핑크|그린|아이보리|와인|퍼플|옐로우|옐로|민트|버건디|크림|오렌지)/;
const STATUS_WORDS = /(전시상품|미사용|새상품|리퍼)/;

// 모델번호형 토큰: 영숫자 5자 이상 + 숫자와 영문이 섞임
const isModelLike = (token) =>
  /^[A-Za-z0-9][A-Za-z0-9.\-]{4,}$/.test(token) &&
  /\d/.test(token) &&
  /[A-Za-z]/.test(token);

/**
 * 의류 상품명 규칙 위반 경고 목록 반환 (비차단 — 저장은 막지 않음)
 * @param {string} name 상품명
 * @returns {string[]} 경고 문구 배열
 */
export function getClothesNameWarnings(name) {
  const warnings = [];
  if (!name || !name.trim()) return warnings;

  const tokens = name.trim().split(/\s+/);
  const last = tokens[tokens.length - 1];

  if (/\s{2,}/.test(name)) {
    warnings.push("공백이 연속으로 두 번 입력되어 있습니다.");
  }
  if (name.includes("_")) {
    warnings.push("'_' 구분자는 사용하지 않습니다. 공백으로 구분해 주세요.");
  }
  if (STATUS_WORDS.test(name)) {
    warnings.push(
      "상태 문구(전시상품·미사용 등)는 상품명이 아닌 등급/비고에 입력해 주세요."
    );
  }

  // 동의어 병기 감지
  for (let i = 0; i < tokens.length - 1; i++) {
    for (const [a, b] of SYNONYM_PAIRS) {
      if (
        (tokens[i] === a && tokens[i + 1] === b) ||
        (tokens[i] === b && tokens[i + 1] === a)
      ) {
        warnings.push(
          `동의어 병기 금지: '${tokens[i]} ${tokens[i + 1]}' → 표준어 하나만 사용해 주세요.`
        );
      }
    }
  }

  // 비표준 표기
  Object.keys(NONSTANDARD_WORDS).forEach((bad) => {
    if (tokens.includes(bad)) {
      warnings.push(
        `'${bad}' 대신 표준 표기 '${NONSTANDARD_WORDS[bad]}'을(를) 사용해 주세요.`
      );
    }
  });
  if (NONSTANDARD_LAST[last]) {
    warnings.push(`아이템 종류는 '${NONSTANDARD_LAST[last]}'(으)로 통일합니다.`);
  } else if (
    tokens.length >= 2 &&
    /[가-힣]/.test(last) &&
    !CLOTHES_ITEM_LIST.includes(last)
  ) {
    // 아이템 종류 통일값 검사 — 마지막 단어가 표준 목록 밖이면 안내 (비차단)
    warnings.push(
      `아이템 종류 '${last}'은(는) 표준 목록에 없습니다. 구성 입력의 아이템 목록에서 선택해 주세요.`
    );
  }

  // 모델번호: 소문자 감지
  if (tokens.some((t) => isModelLike(t) && /[a-z]/.test(t))) {
    warnings.push("모델번호는 영문 대문자로 입력해 주세요.");
  }

  // 모델번호가 끝에 위치 (상품명은 아이템 종류로 끝나야 함)
  if (tokens.length >= 2 && /\d/.test(last) && !/[가-힣]/.test(last)) {
    warnings.push(
      "모델번호는 브랜드명 바로 뒤에 위치합니다. 상품명은 아이템 종류로 끝나야 합니다."
    );
  }

  // 컬러 안내 (시그니처 컬러만 허용)
  if (COLOR_WORDS.test(name)) {
    warnings.push(
      "컬러는 원칙적으로 컬러 입력란에 입력합니다. 시그니처 컬러(상품 특징)일 때만 상품명에 허용됩니다."
    );
  }

  return warnings;
}

/**
 * 성별 선택값(products.gender)과 상품명 내 성별 표기의 불일치 검사 (비차단 경고)
 * - 상품명에 성별 표기가 없으면 성별 선택과 무관하게 통과 (규칙상 성별 표기는 선택 사항)
 * - 상품명에 표기가 있는데 성별 미선택 → 선택 유도 / 선택값과 다름 → 불일치 경고
 * @param {string} name 상품명
 * @param {string} gender 선택된 성별 (남성/여성/키즈, ''=공용/미지정)
 * @returns {string|null} 경고 문구 (문제 없으면 null)
 */
export function getGenderMismatchWarning(name, gender) {
  if (!name || !name.trim()) return null;

  const found = [];
  for (const token of name.trim().split(/\s+/)) {
    const match = token.match(/^(남성|여성|키즈)(용)?$/);
    if (match && !found.includes(match[1])) found.push(match[1]);
  }
  if (found.length === 0) return null;

  const selected = (gender || "").trim();
  if (!selected) {
    return `상품명에 '${found[0]}' 표기가 있지만 성별이 선택되지 않았습니다. 성별을 선택해 주세요.`;
  }
  if (!found.includes(selected)) {
    return `성별 선택(${selected})과 상품명 표기(${found.join(", ")})가 다릅니다.`;
  }
  return null;
}
